import { Option } from '@sapphire/result';

import {
  INITIAL_LOCATION_UPLOAD_RESULT,
  isSelectableQualification,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { SurveyCreationChildAudienceState } from '../../survey-creation-state.model';

export interface LoadedLocationGroups {
  readonly qualificationCode: number;
  readonly filePath: Nullable<string>;
  readonly groupedCodes: Record<string, string[]>;
}

// `none` means the response is stale and state must stay as the buyer left it: the qualification
// was cleared or swapped, a newer upload is in flight or landed, or the file moved on. Valid codes
// come from the live qualification, not from the survey the request was made for, so the section's
// upload-sync sees an unchanged selection and leaves the quotas alone.
export function hydrateLocationGroups(
  child: SurveyCreationChildAudienceState,
  loaded: LoadedLocationGroups,
): Option<Pick<SurveyCreationChildAudienceState, 'locationFileUpload'>> {
  const upload = child.locationFileUpload;

  return Option.from(
    child.qualifications.value
      .filter(isSelectableQualification)
      .find(
        (qualification) =>
          qualification.qualificationCode === loaded.qualificationCode &&
          !qualification.isDefault,
      ),
  )
    .filter(
      () =>
        !upload.loading &&
        !upload.result &&
        upload.filePath === loaded.filePath,
    )
    .map(({ data: validCodes }) => ({
      locationFileUpload: {
        qualificationCode: loaded.qualificationCode,
        loading: false,
        error: null,
        filePath: loaded.filePath,
        result: {
          ...INITIAL_LOCATION_UPLOAD_RESULT,
          validCodes,
          totalCodeCount: validCodes.length,
          correctCodeCount: validCodes.length,
          filePath: loaded.filePath ?? '',
          groupedCodes: loaded.groupedCodes,
        },
      },
    }));
}
